import { useTranslations } from 'next-intl';
import { type ElementType, Fragment, type ReactNode } from 'react';

import type messages from 'src/messages/en.json';

export type Messages = typeof messages;

type Join<K, P> = K extends string | number
  ? P extends string | number
    ? `${K}.${P}`
    : never
  : never;

export type NamespacedKeys<T> = {
  [K in keyof T & (string | number)]: T[K] extends object
    ? Join<K, NamespacedKeys<T[K]>>
    : K;
}[keyof T & (string | number)];

export type MessageKeys = NamespacedKeys<Messages>;

type RichTranslationValues = Record<
  string,
  ReactNode | ((chunks: ReactNode) => ReactNode)
>;

type TranslationProps = {
  id: MessageKeys;
  values?: RichTranslationValues;
  defaultMessage?: string;
  as?: ElementType;
  [key: string]: any;
};

export type Namespaces = keyof Messages;

export const FormattedMessage = ({
  id,
  values,
  defaultMessage,
  as: Component = Fragment,
  ...rest
}: TranslationProps) => {
  const t = useTranslations();

  let translated: ReactNode = defaultMessage ?? id;

  try {
    translated = t.rich(id, {
      br: <br key={id} />,
      b: (chunks) => <b>{chunks}</b>,
      ...(values as any),
    });
  } catch (e) {
    console.error(`[i18n] Missing translation: ${id}`, e);
  }

  if (Component === Fragment) {
    return <>{translated}</>;
  }

  return (
    <Component id={id} {...rest}>
      {translated}
    </Component>
  );
};
